import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Monitor, FileSpreadsheet, Server, Activity, MapPin, Zap } from "lucide-react";
import { useTranslation } from "react-i18next";

function DeviceCard({ dev, API_BASE, onPenaltyChange }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [ratios, setRatios] = useState(null);
  const [realtime, setRealtime] = useState(null);

  // Oranlar
  useEffect(() => {
    async function fetchRatios() {
      try {
        let endpoint = null;
        if (dev.type === "Grup Arge Klasik Röle") {
          endpoint = `${API_BASE}/gruparge/ratios?deviceId=${dev.id}`;
        } else if (dev.type === "PRC-15H+TCR") {
          endpoint = `${API_BASE}/reactive/ratios?deviceId=${dev.id}`;
        } else {
          return;
        }

        const res = await fetch(endpoint);
        if (!res.ok) throw new Error("HTTP " + res.status);
        const json = await res.json();

        setRatios(json);
      } catch (err) {
        console.error("❌ ratios fetch error:", err);
      }
    }

    fetchRatios();
    const i = setInterval(fetchRatios, 5000);
    return () => clearInterval(i);
  }, [API_BASE, dev.id, dev.type]);

  // Realtime voltaj/akım
  useEffect(() => {
    async function fetchRealtime() {
      try {
        let url;

        if (["PAD-01", "PAD-02", "PAD-03"].includes(dev.type)) {
          url = `${API_BASE}/measurements/realtime-lite?deviceId=${dev.id}`;
        } else if (dev.type === "PRC-15H+TCR") {
          url = `${API_BASE}/reactive/realtime-lite?deviceId=${dev.id}`;
        } else if (dev.type === "PMA-03") {
          url = `${API_BASE}/multimeter/realtime-lite?deviceId=${dev.id}`;
        } else if (dev.type === "Grup Arge Klasik Röle") {
          url = `${API_BASE}/gruparge/realtime-lite?deviceId=${dev.id}`;
        } else if (dev.type === "Voltampermetre") {
          url = `${API_BASE}/voltampermetre/realtime-lite?deviceId=${dev.id}`;
        } else return;

        const res = await fetch(url);
        if (!res.ok) throw new Error("HTTP " + res.status);

        const json = await res.json();

        setRealtime(json);
      } catch (err) {
        console.error("❌ realtime fetch error:", err);
      }
    }

    fetchRealtime();
    const i = setInterval(fetchRealtime, 5000);
    return () => clearInterval(i);
  }, [API_BASE, dev.id, dev.type]);


  // Toplam değerleri backend'den gelen endüktif/kapasitif alanlarından al
  const endVal = ratios?.tanPhiEnd ? ratios.tanPhiEnd.toFixed(3) : "0.000";
  const capVal = ratios?.tanPhiCap
    ? Math.abs(ratios.tanPhiCap).toFixed(3)
    : "0.000";

  const isAnalyzer = ["PAD-01", "PAD-02", "PAD-03"].includes(dev.type);
  const isReactive = dev.type === "PRC-15H+TCR";
  const isMultimeter = dev.type === "PMA-03";
  const isGrupArge = dev.type === "Grup Arge Klasik Röle";
  const isVoltampermetre = dev.type === "Voltampermetre";
  const showRatioValues = isReactive || isGrupArge;

  const endNum = Number(ratios?.tanPhiEnd || 0);
  const capNum = Math.abs(Number(ratios?.tanPhiCap || 0));
  const isInductivePenalty = showRatioValues && endNum > 20;
  const isCapacitivePenalty = showRatioValues && capNum > 15;
  const hasPenalty = isInductivePenalty || isCapacitivePenalty;

  useEffect(() => {
    if (onPenaltyChange && showRatioValues) {
      onPenaltyChange(dev.id, hasPenalty);
    }
  }, [hasPenalty, dev.id, onPenaltyChange, showRatioValues]);

  const penaltyLabel = isInductivePenalty && isCapacitivePenalty
    ? t("ratios.penaltyBoth")
    : isInductivePenalty
      ? t("ratios.penaltyInductive")
      : isCapacitivePenalty
        ? t("ratios.penaltyCapacitive")
        : t("ratios.noPenalty");

  // Son haberleşme 30 dk'dan eski mi?
  const lastCommDate = dev.last_comm ? new Date(dev.last_comm) : null;
  const diffMs = lastCommDate ? new Date() - lastCommDate : 0;
  const isOffline = dev.last_comm && diffMs > 30 * 60 * 1000;
  const latDisplay = Number.isFinite(Number(dev.latitude))
    ? Number(dev.latitude).toFixed(5)
    : dev.latitude;
  const lonDisplay = Number.isFinite(Number(dev.longitude))
    ? Number(dev.longitude).toFixed(5)
    : dev.longitude;

  return (
    <div className={`rounded-2xl shadow-md p-6 hover:shadow-lg transition cursor-pointer border-2 ${
      hasPenalty ? "bg-red-50 border-red-300" : "bg-white border-transparent"
    }`}>
      <div className="flex justify-between items-start">
        <div>
          <h2 className="text-xl font-semibold text-indigo-600">{dev.name}</h2>
          <p className="text-gray-600 text-sm mt-2">{t('common.device')}: {dev.type}</p>
        </div>

        {dev.latitude && dev.longitude && (
          <a
            href={`https://www.google.com/maps/search/?api=1&query=${dev.latitude},${dev.longitude}`}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center text-xs text-gray-400 hover:text-indigo-600 transition-colors"
            title={t('gatewayList.openInMaps')}
            onClick={(e) => e.stopPropagation()}
          >
            <MapPin className="w-4 h-4 mr-1" />
            <span>{latDisplay}, {lonDisplay}</span>
          </a>
        )}
      </div>

      {dev.description && (
        <p className="text-gray-400 text-xs mt-2">{dev.description}</p>
      )}

      {showRatioValues && hasPenalty && (
        <div className="mt-2">
          <span className="inline-flex items-center px-2 py-1 rounded-md text-xs font-bold bg-red-600 text-white">
            {t("ratios.penalty")}: {penaltyLabel}
          </span>
        </div>
      )}

      {/* son haberleşme */}
      {dev.last_comm ? (
        <p className={`text-xs mt-2 ${isOffline ? "text-red-600 font-bold" : "text-gray-400"}`}>
          {t('common.lastComm')}:{" "}
          {new Date(dev.last_comm).toLocaleString("tr-TR", {
            dateStyle: "short",
            timeStyle: "short",
          })}
        </p>
      ) : (
        <p className="text-xs text-red-400 mt-2">{t('common.lastComm')}: {t('common.none')}</p>
      )}

      {/* Oranlar sadece reactive ve grup arge kartlarinda gorunur, digerlerinde bosluk korunur */}
      <div className={`flex justify-between mt-4 text-sm font-semibold min-h-[24px] ${showRatioValues ? '' : 'invisible'}`}>
        <span className="text-green-600">{t('dashboard.inductive')}: {endVal} %</span>
        <span className="text-red-600">{t('dashboard.capacitive')}: {capVal} %</span>
      </div>

      {/* Ekstra: Voltajlar ve Akımlar */}
      {realtime && !isVoltampermetre && (
        <div className="mt-4">
          {/* Voltajlar */}
          <p className="font-medium text-gray-700 mb-2">{t('common.voltages')} (V)</p>
          <div className="grid grid-cols-3 gap-2">
            <div className="bg-gray-50 rounded-md p-2 text-center shadow-sm">
              <p className="text-xs text-gray-500">L1</p>
              <p className="font-semibold text-indigo-600">
                {realtime.voltages?.VL1 != null ? realtime.voltages.VL1.toFixed(2) : "-"}
              </p>
            </div>
            <div className="bg-gray-50 rounded-md p-2 text-center shadow-sm">
              <p className="text-xs text-gray-500">L2</p>
              <p className="font-semibold text-indigo-600">
                {realtime.voltages?.VL2 != null ? realtime.voltages.VL2.toFixed(2) : "-"}
              </p>
            </div>
            <div className="bg-gray-50 rounded-md p-2 text-center shadow-sm">
              <p className="text-xs text-gray-500">L3</p>
              <p className="font-semibold text-indigo-600">
                {realtime.voltages?.VL3 != null ? realtime.voltages.VL3.toFixed(2) : "-"}
              </p>
            </div>
          </div>

          {/* Akımlar */}
          <p className="font-medium text-gray-700 mt-4 mb-2">{t('common.currents')} (A)</p>
          <div className="grid grid-cols-3 gap-2">
            <div className="bg-gray-50 rounded-md p-2 text-center shadow-sm">
              <p className="text-xs text-gray-500">L1</p>
              <p className="font-semibold text-green-600">
                {realtime.currents?.I1 != null ? realtime.currents.I1.toFixed(2) : "-"}
              </p>
            </div>
            <div className="bg-gray-50 rounded-md p-2 text-center shadow-sm">
              <p className="text-xs text-gray-500">L2</p>
              <p className="font-semibold text-green-600">
                {realtime.currents?.I2 != null ? realtime.currents.I2.toFixed(2) : "-"}
              </p>
            </div>
            <div className="bg-gray-50 rounded-md p-2 text-center shadow-sm">
              <p className="text-xs text-gray-500">L3</p>
              <p className="font-semibold text-green-600">
                {realtime.currents?.I3 != null ? realtime.currents.I3.toFixed(2) : "-"}
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Voltampermetre: tek faz V + I */}
      {realtime && isVoltampermetre && (
        <div className="mt-4 grid grid-cols-2 gap-3">
          <div className="bg-gray-50 rounded-md p-3 text-center shadow-sm">
            <p className="text-xs text-gray-500 mb-1">{t('common.voltages')} (V)</p>
            <p className="font-semibold text-indigo-600 text-lg">
              {realtime.V != null ? Number(realtime.V).toFixed(2) : "-"}
            </p>
          </div>
          <div className="bg-gray-50 rounded-md p-3 text-center shadow-sm">
            <p className="text-xs text-gray-500 mb-1">{t('common.currents')} (A)</p>
            <p className="font-semibold text-green-600 text-lg">
              {realtime.I != null ? Number(realtime.I).toFixed(2) : "-"}
            </p>
          </div>
        </div>
      )}


      {/* Butonlar */}
      <div className="flex gap-3 mt-4">
        <button
          onClick={() => {
            const type = isAnalyzer
              ? "analyzer"
              : isReactive
                ? "reactive"
                : isMultimeter
                  ? "multimeter"
                  : isGrupArge
                    ? "gruparge"
                    : isVoltampermetre
                      ? "voltampermetre"
                      : null;
            if (!type) return;

            // 🔑 cihaz tipini kaydet
            localStorage.removeItem("selectedDevice");
            localStorage.setItem("deviceType", type);
            localStorage.setItem("deviceModel", dev.type);

            // 🔑 doğru sayfaya yönlendir
            navigate(`/devices/${dev.id}/${type}/real-time`);
          }}
          className="flex-1 bg-indigo-500 text-white px-4 py-2 rounded-md text-sm hover:bg-indigo-600"
        >
          {t('common.data')}
        </button>


      </div>
    </div>
  );
}

export default function DeviceList() {
  const { t } = useTranslation();
  const { gatewayId } = useParams();
  const [devices, setDevices] = useState([]);
  const [devicePenalties, setDevicePenalties] = useState({});
  const [penaltyFilter, setPenaltyFilter] = useState("all");
  const API_BASE = process.env.REACT_APP_API_BASE_URL;

  const handlePenaltyChange = (deviceId, hasPenalty) => {
    setDevicePenalties((prev) => {
      if (prev[deviceId] === hasPenalty) return prev;
      return { ...prev, [deviceId]: hasPenalty };
    });
  };

  useEffect(() => {
    async function fetchDevices() {
      try {
        const token = localStorage.getItem("token");
        const res = await fetch(`${API_BASE}/gateways/${gatewayId}/devices`, {
          headers: {
            "Content-Type": "application/json",
            "Authorization": `Bearer ${token}`,
          },
        });

        if (!res.ok) throw new Error(`HTTP ${res.status}`);

        const data = await res.json();

        // Eğer backend array dönüyorsa:
        setDevices(Array.isArray(data) ? data : []);

        // Eğer backend {devices: [...] } dönüyorsa:
        // setDevices(data.devices || []);
      } catch (err) {
        console.error("Error fetching devices:", err);
      }
    }

    fetchDevices();
  }, [API_BASE, gatewayId]);

  // 🔹 Tür bazlı sayılar
  const analyzerCount = devices.filter((d) =>
    ["PAD-01", "PAD-02", "PAD-03"].includes(d.type)
  ).length;
  const reactiveCount = devices.filter((d) => d.type === "PRC-15H+TCR").length;
  const multimeterCount = devices.filter((d) => d.type === "PMA-03").length;
  const grupArgeCount = devices.filter((d) => d.type === "Grup Arge Klasik Röle").length;
  const voltampermetreCount = devices.filter((d) => d.type === "Voltampermetre").length;


  const reactiveDevices = devices.filter(
    (d) => d.type === "PRC-15H+TCR" || d.type === "Grup Arge Klasik Röle"
  );
  const penaltyDeviceCount = reactiveDevices.filter((d) => devicePenalties[d.id] === true).length;

  const filteredDevices = devices.filter((dev) => {
    const isReactiveDev = dev.type === "PRC-15H+TCR" || dev.type === "Grup Arge Klasik Röle";
    if (penaltyFilter === "penalty") return isReactiveDev && devicePenalties[dev.id] === true;
    if (penaltyFilter === "ok") return !isReactiveDev || devicePenalties[dev.id] !== true;
    return true;
  });

  return (
    <div className="p-4 md:p-8 min-h-screen bg-gray-50">
      <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
        <h1 className="text-2xl font-bold text-gray-800">{t('deviceList.title')}</h1>

        {/* Penalty Filter — only shown when reactive devices exist */}
        {reactiveDevices.length > 0 && (
          <div className="flex items-center gap-2">
            {["all", "penalty", "ok"].map((val) => {
              const label =
                val === "all" ? t("ratios.filterAll") :
                val === "penalty" ? t("ratios.filterPenaltyOnly") :
                t("ratios.filterNoPenalty");
              const active = penaltyFilter === val;
              return (
                <button
                  key={val}
                  onClick={() => setPenaltyFilter(val)}
                  className={`px-3 py-1.5 rounded-md text-sm font-medium transition border ${
                    active
                      ? val === "penalty"
                        ? "bg-red-600 text-white border-red-600"
                        : "bg-indigo-600 text-white border-indigo-600"
                      : "bg-white text-gray-600 border-gray-300 hover:border-gray-400"
                  }`}
                >
                  {label}
                  {val === "penalty" && penaltyDeviceCount > 0 && (
                    <span className={`ml-1.5 text-xs font-bold px-1.5 py-0.5 rounded-full ${active ? "bg-white text-red-600" : "bg-red-100 text-red-600"}`}>
                      {penaltyDeviceCount}
                    </span>
                  )}
                </button>
              );
            })}
          </div>
        )}
      </div>

      {/* Özet Kartlar */}
      <div className="mb-8 flex flex-wrap gap-4">
        {analyzerCount > 0 && (
          <div className="flex bg-white rounded-xl shadow-md overflow-hidden w-full sm:w-64">
            <div className="bg-indigo-500 flex items-center justify-center w-20">
              <Monitor className="w-8 h-8 text-white" />
            </div>
            <div className="flex-1 text-center py-4">
              <p className="text-2xl font-bold text-gray-800">
                {analyzerCount}
              </p>
              <p className="text-gray-500 text-sm">{t('common.analyzer')}</p>
            </div>
          </div>
        )}

        {reactiveCount > 0 && (
          <div className="flex bg-white rounded-xl shadow-md overflow-hidden w-full sm:w-64">
            <div className="bg-purple-500 flex items-center justify-center w-20">
              <Server className="w-8 h-8 text-white" />
            </div>
            <div className="flex-1 text-center py-4">
              <p className="text-2xl font-bold text-gray-800">
                {reactiveCount}
              </p>
              <p className="text-gray-500 text-sm">{t('common.reactive')}</p>
            </div>
          </div>
        )}
        {multimeterCount > 0 && (
          <div className="flex bg-white rounded-xl shadow-md overflow-hidden w-full sm:w-64">
            <div className="bg-green-500 flex items-center justify-center w-20">
              <Activity className="w-8 h-8 text-white" />
            </div>
            <div className="flex-1 text-center py-4">
              <p className="text-2xl font-bold text-gray-800">{multimeterCount}</p>
              <p className="text-gray-500 text-sm">{t('common.multimeter')}</p>
            </div>
          </div>
        )}
        {grupArgeCount > 0 && (
          <div className="flex bg-white rounded-xl shadow-md overflow-hidden w-full sm:w-64">
            <div className="bg-orange-500 flex items-center justify-center w-20">
              <FileSpreadsheet className="w-8 h-8 text-white" />
            </div>
            <div className="flex-1 text-center py-4">
              <p className="text-2xl font-bold text-gray-800">{grupArgeCount}</p>
              <p className="text-gray-500 text-sm">{t('common.gruparge')}</p>
            </div>
          </div>
        )}
        {voltampermetreCount > 0 && (
          <div className="flex bg-white rounded-xl shadow-md overflow-hidden w-full sm:w-64">
            <div className="bg-yellow-500 flex items-center justify-center w-20">
              <Zap className="w-8 h-8 text-white" />
            </div>
            <div className="flex-1 text-center py-4">
              <p className="text-2xl font-bold text-gray-800">{voltampermetreCount}</p>
              <p className="text-gray-500 text-sm">{t('common.voltampermetre')}</p>
            </div>
          </div>
        )}
      </div>

      {/* Cihaz Kartları */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
        {filteredDevices.map((dev) => (
          <DeviceCard key={dev.id} dev={dev} API_BASE={API_BASE} onPenaltyChange={handlePenaltyChange} />
        ))}
      </div>
    </div>
  );
}
